import type { ReactNode, SVGProps } from 'react';

/**
 * Ícones da app web: traço de 1,8 com pontas redondas, na cor do texto (os mesmos do
 * sininho e do menu). Decorativos: o nome acessível vem do botão ou da ligação.
 */
function Icon({ children, size = 18, ...rest }: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

type P = SVGProps<SVGSVGElement> & { size?: number };
const make = (paths: ReactNode) =>
  function NamedIcon(p: P) {
    return <Icon {...p}>{paths}</Icon>;
  };

export const HomeIcon = make(
  <path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4.5v-5.5h-5V20H5a1 1 0 0 1-1-1v-8.5z" />,
);
/** Os meus guias: uma folha com o N de quatro nós (a assinatura Nolens). */
export const MyGuidesIcon = make(
  <>
    <rect x="4.5" y="3.5" width="15" height="17" rx="3" />
    <path d="M9 15.5v-7l6 7v-7" />
  </>,
);
export const GuidesIcon = make(
  <>
    <rect x="3.5" y="5" width="13" height="15" rx="2.5" />
    <path d="M7.5 2.5h10a3 3 0 0 1 3 3v11" />
  </>,
);
export const StatsIcon = make(<path d="M5 20V12M10 20V6M15 20v-9M20 20V4" />);
export const MembersIcon = make(
  <>
    <circle cx="9" cy="8.5" r="3.2" />
    <path d="M3.5 19.5c.6-3 2.8-4.8 5.5-4.8s4.9 1.8 5.5 4.8" />
    <path d="M15.5 5.6a3.2 3.2 0 0 1 0 5.8M17.5 14.9c1.6.6 2.7 2.2 3 4.6" />
  </>,
);
export const SettingsIcon = make(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.8v2.4M12 18.8v2.4M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M2.8 12h2.4M18.8 12h2.4M4.9 19.1l1.7-1.7M17.4 6.6l1.7-1.7" />
  </>,
);
export const SearchIcon = make(
  <>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4 4" />
  </>,
);
export const BellIcon = make(
  <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15L6 16zM10 20a2 2 0 0 0 4 0" />,
);
export const PlusIcon = make(<path d="M12 5v14M5 12h14" />);
export const MenuIcon = make(<path d="M4 7h16M4 12h16M4 17h16" />);
export const PanelIcon = make(
  <>
    <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
    <path d="M9.5 4.5v15" />
  </>,
);
export const ChevronDownIcon = make(<path d="m7 10 5 5 5-5" />);
export const ChevronRightIcon = make(<path d="m10 7 5 5-5 5" />);
export const ArrowLeftIcon = make(<path d="M19 12H5M11 6l-6 6 6 6" />);
export const ExtensionIcon = make(
  <path d="M9 4.5a2 2 0 1 1 4 0V6h3.5a1.5 1.5 0 0 1 1.5 1.5V11h-1.5a2 2 0 1 0 0 4H18v3.5a1.5 1.5 0 0 1-1.5 1.5H13v-1.5a2 2 0 1 0-4 0V20H5.5A1.5 1.5 0 0 1 4 18.5V15h1.5a2 2 0 1 0 0-4H4V7.5A1.5 1.5 0 0 1 5.5 6H9V4.5z" />,
);
export const LaptopIcon = make(
  <path d="M5.5 6.5A1.5 1.5 0 0 1 7 5h10a1.5 1.5 0 0 1 1.5 1.5V15h-13V6.5zM3.5 15h17l-1 3a1.5 1.5 0 0 1-1.4 1H5.9a1.5 1.5 0 0 1-1.4-1l-1-3z" />,
);
export const ShieldIcon = make(
  <path d="M12 3.5 5 6v5.5c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9V6l-7-2.5z" />,
);
export const SignOutIcon = make(
  <path d="M14 4.5h3.5a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H14M10 16l4-4-4-4M14 12H4" />,
);
export const GridIcon = make(
  <>
    <rect x="4" y="4" width="6.5" height="6.5" rx="1.6" />
    <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.6" />
    <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.6" />
    <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.6" />
  </>,
);
export const ListIcon = make(
  <path d="M9 6.5h11M9 12h11M9 17.5h11M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" />,
);
export const LinkIcon = make(
  <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />,
);
export const CheckIcon = make(<path d="m5 12.5 4.5 4.5L19 7.5" />);
export const DownloadIcon = make(<path d="M12 4v11M7 10l5 5 5-5M5 20h14" />);
export const UserPlusIcon = make(
  <>
    <circle cx="10" cy="8.5" r="3.5" />
    <path d="M3.5 19.5c.7-3.2 3.3-5 6.5-5s5.8 1.8 6.5 5M19 8v6M16 11h6" />
  </>,
);
export const BrandIcon = make(
  <>
    <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.2 0 1.8-.8 1.8-1.7 0-1.1-.9-1.5-.9-2.6 0-1 .8-1.7 1.8-1.7h1.8a3.5 3.5 0 0 0 3.5-3.5c0-4.1-3.6-7.5-8-7.5z" />
    <circle cx="8" cy="11" r="1" />
    <circle cx="11" cy="7.5" r="1" />
    <circle cx="15.5" cy="8.5" r="1" />
  </>,
);
export const SlidersIcon = make(
  <path d="M5 6.5h8M17 6.5h2M5 12h2M11 12h8M5 17.5h10M19 17.5h0M15 4.5v4M9 10v4M17 15.5v4" />,
);
export const LockIcon = make(
  <>
    <rect x="5" y="10.5" width="14" height="9.5" rx="2.5" />
    <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
  </>,
);
export const CardIcon = make(
  <>
    <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
    <path d="M3.5 10h17M7.5 15h3" />
  </>,
);
export const UserIcon = make(
  <>
    <circle cx="12" cy="8.5" r="3.8" />
    <path d="M5 20c.8-3.6 3.6-5.7 7-5.7s6.2 2.1 7 5.7" />
  </>,
);
export const SparkIcon = make(
  <path d="M12 3.5 13.8 10l6.7 2-6.7 2L12 20.5 10.2 14l-6.7-2 6.7-2L12 3.5z" />,
);
export const ClockIcon = make(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </>,
);
export const EyeIcon = make(
  <>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="2.8" />
  </>,
);
/** Gravar: o ponto da gravação dentro de um anel. */
export function RecordIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="4.5" fill="currentColor" />
    </svg>
  );
}
